import type { TrendsCategory } from "../types";
import { CLOUD_COSTS_LABEL } from "../../cloudCost/cloudCostTaxonomy";

// Layout props for the summary time-series bar charts (Current/Future State).
// Their x-axis shows rotated date labels and the daily view can pack hundreds
// of bars, so they need a taller axis band, bars that stay visible at any
// density, and a legend tucked directly under the labels. Other Chart
// consumers keep the component defaults.
export const SUMMARY_TIME_SERIES_CHART_PROPS = {
  adaptiveBarSize: true,
  xAxisHeight: 50,
  chartProps: { margin: { top: 25, bottom: 5, right: 5 } },
  legendProps: { wrapperStyle: { bottom: -24 } },
};

export const TRENDS_GROUPS = [
  "Clusters",
  "SQL Warehouse",
  "AI/ML",
  "Lakehouse",
  "Platform",
] as const;

export type TrendsGroup = (typeof TRENDS_GROUPS)[number];

// "Cloud Costs" is a distinct lens (the cloud-provider bill broken into infra
// line items), not a Databricks DBU group. It's appended to the category
// selector and rendered by a dedicated chart, but kept out of TRENDS_GROUPS so
// the DBU color/product maps stay unaffected.
export const CLOUD_COSTS_GROUP = CLOUD_COSTS_LABEL;

// Full ordered option list for the cost category selector: the five DBU groups
// followed by the Cloud Costs lens. Gated behind `showCloudCosts` at the call
// site, which falls back to TRENDS_GROUPS when the flag is off.
export const COST_CATEGORY_OPTIONS: readonly string[] = [...TRENDS_GROUPS, CLOUD_COSTS_GROUP];

export const GROUP_TO_CATEGORY: Record<TrendsGroup, TrendsCategory> = {
  Clusters: "clusters",
  "SQL Warehouse": "sql_warehouse",
  "AI/ML": "ai_ml",
  Lakehouse: "lakehouse",
  Platform: "platform",
};

export const PRODUCT_GROUPS: Record<TrendsGroup, string[]> = {
  Clusters: ["JOBS", "ALL_PURPOSE", "DLT", "INTERACTIVE"],
  "SQL Warehouse": ["Classic", "Pro", "Serverless"],
  "AI/ML": [
    "MODEL_SERVING",
    "VECTOR_SEARCH",
    "AI_GATEWAY",
    "AGENT_BRICKS",
    "AI_RUNTIME",
    "AI_FUNCTIONS",
    "FOUNDATION_MODEL_TRAINING",
    "AGENT_EVALUATION",
  ],
  Lakehouse: [
    "DATABASE",
    "ONLINE_TABLES",
    "PREDICTIVE_OPTIMIZATION",
    "DATA_QUALITY_MONITORING",
    "LAKEHOUSE_MONITORING",
    "DATA_CLASSIFICATION",
  ],
  Platform: [
    "NETWORKING",
    "BASE_ENVIRONMENTS",
    "FINE_GRAINED_ACCESS_CONTROL",
    "APPS",
    "CLEAN_ROOM",
    "LAKEFLOW_CONNECT",
  ],
};

const PRODUCT_TO_GROUP: Record<string, TrendsGroup> = {};
for (const group of TRENDS_GROUPS) {
  for (const product of PRODUCT_GROUPS[group]) {
    PRODUCT_TO_GROUP[product] = group;
  }
}

export type TrendsCategoryKey = Exclude<TrendsCategory, "total">;

// Maps a canonical category key to its human-readable group label.
export const CATEGORY_TO_GROUP: Record<TrendsCategoryKey, TrendsGroup> = {
  clusters: "Clusters",
  sql_warehouse: "SQL Warehouse",
  ai_ml: "AI/ML",
  lakehouse: "Lakehouse",
  platform: "Platform",
};

const DEFAULT_COLOR = "#9E9E9E";
const ESTIMATE_OPACITY = 0.55;

const GROUP_COLORS: Record<TrendsGroup, string> = {
  Clusters: "#5B8DEF",
  "SQL Warehouse": "#4CAF50",
  "AI/ML": "#9575CD",
  Lakehouse: "#F5A623",
  Platform: "#78909C",
};

const PRODUCT_COLORS: Record<TrendsGroup, Record<string, string>> = {
  Clusters: {
    JOBS: "#5B8DEF",
    ALL_PURPOSE: "#26A69A",
    DLT: "#AB47BC",
    INTERACTIVE: "#EF5350",
  },
  "SQL Warehouse": {
    Classic: "#01CD8C",
    Pro: "#5B9FFF",
    Serverless: "#FF754C",
  },
  "AI/ML": {
    MODEL_SERVING: "#7E57C2",
    VECTOR_SEARCH: "#EC407A",
    AI_GATEWAY: "#29B6F6",
    AGENT_BRICKS: "#26A69A",
    AI_RUNTIME: "#FF7043",
    AI_FUNCTIONS: "#66BB6A",
    FOUNDATION_MODEL_TRAINING: "#FFCA28",
    AGENT_EVALUATION: "#5C6BC0",
  },
  Lakehouse: {
    DATABASE: "#FF9800",
    ONLINE_TABLES: "#42A5F5",
    PREDICTIVE_OPTIMIZATION: "#AB47BC",
    DATA_QUALITY_MONITORING: "#EF5350",
    LAKEHOUSE_MONITORING: "#66BB6A",
    DATA_CLASSIFICATION: "#26A69A",
  },
  Platform: {
    NETWORKING: "#5C6BC0",
    BASE_ENVIRONMENTS: "#29B6F6",
    FINE_GRAINED_ACCESS_CONTROL: "#AB47BC",
    APPS: "#26A69A",
    CLEAN_ROOM: "#66BB6A",
    LAKEFLOW_CONNECT: "#FF7043",
  },
};

const isTrendsGroup = (value: string): value is TrendsGroup =>
  (TRENDS_GROUPS as readonly string[]).includes(value);

export const asTrendsGroup = (value: string | null | undefined): TrendsGroup | null =>
  value && isTrendsGroup(value) ? value : null;

// Accepts either a group label ("Clusters") or a canonical category key
// ("clusters") and resolves it to the group label.
const resolveGroup = (groupOrKey: string): TrendsGroup | null => {
  if (isTrendsGroup(groupOrKey)) return groupOrKey;
  return CATEGORY_TO_GROUP[groupOrKey as TrendsCategoryKey] ?? null;
};

export const getGroupColor = (groupOrKey: string): string => {
  const group = resolveGroup(groupOrKey);
  return group ? GROUP_COLORS[group] : DEFAULT_COLOR;
};

export const getProductColor = (product: string, groupOrKey: string): string => {
  const group = resolveGroup(groupOrKey);
  if (!group) return DEFAULT_COLOR;
  return PRODUCT_COLORS[group][product] || GROUP_COLORS[group];
};

export const getProductGroup = (product: string): TrendsGroup | "Other" =>
  PRODUCT_TO_GROUP[product] || "Other";

/**
 * Resolves any Databricks chart-data key to a hex color. Strips `product_`
 * prefixes and `_estimate` suffixes, then looks up product or group color.
 */
export const databricksColorFor = (key: string): string => {
  const base = key.replace(/^product_/, "").replace(/_estimate$/, "");
  const group = getProductGroup(base);
  if (group !== "Other") return getProductColor(base, group);
  return getGroupColor(base);
};

export { ESTIMATE_OPACITY };
